// farm-booking/frontend/src/pages/HomePage.jsx
// Page d'accueil avec OpenStreetMap - La Ferme du Petit Essart
// 73+ Papy - AVEC ICÔNE PNG DÉTOURÉ + Triple-clic admin

import { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import { useNavigate } from 'react-router-dom'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './HomePage.css'

// 🆕 Marqueur personnalisé avec IMAGE PNG (au lieu de l'emoji)
// Uploader ton PNG détouré dans /uploads/produits/marker-ferme.png
const fermeIcon = L.icon({
  iconUrl: '/uploads/produits/marker-ferme.png',
  iconSize: [50, 50],       // Taille affichée
  iconAnchor: [25, 50],     // Point d'ancrage (bas centre de l'image)
  popupAnchor: [0, -50]     // Position du popup par rapport à l'icône
})

function HomePage() {
  const navigate = useNavigate()
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [clickCount, setClickCount] = useState(0)
  const [clickTimer, setClickTimer] = useState(null)

  useEffect(() => {
    fetch('/api/settings')
      .then(res => {
        if (!res.ok) throw new Error('Erreur chargement config')
        return res.json()
      })
      .then(data => {
        setSettings(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Erreur:', err)
        setError(err.message)
        setLoading(false)
      })
  }, [])

  // ⚡ ACCÈS ADMIN DISCRET - Triple-clic sur le titre
  const handleTitleClick = () => {
    setClickCount(prev => prev + 1)
    
    // Réinitialiser le compteur après 1 seconde
    if (clickTimer) clearTimeout(clickTimer)
    const timer = setTimeout(() => setClickCount(0), 1000)
    setClickTimer(timer)
    
    // Si triple-clic détecté → redirection vers /auth
    if (clickCount + 1 === 3) {
      navigate('/auth')
      setClickCount(0)
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="error">
        <h1>⚠️ Erreur</h1>
        <p>{error}</p>
        <p>Vérifiez que l'API fonctionne correctement</p>
      </div>
    )
  }

  // Valeurs par défaut si settings est null
  const headline = settings?.hero_headline || "Bienvenue à La Ferme du Petit Essart"
  const mediaUrl = settings?.hero_media_url || '/uploads/images/placeholder.webp'
  const mediaType = settings?.hero_type || 'image'
  // 🚫 MASQUÉ TEMPORAIREMENT - Décommenter pour réactiver
  // const contactEmail = settings?.contact_email || settings?.email_contact
  // const contactPhone = settings?.contact_phone || settings?.telephone
  const openingHours = settings?.opening_hours || settings?.horaires_distribution
  const address = settings?.adresse || "Rue Champ du Bois, 76530 Grand-Couronne"
  
  // Coordonnées GPS pour OpenStreetMap
  const gpsLat = parseFloat(settings?.gps_latitude || '49.33611443214846')
  const gpsLon = parseFloat(settings?.gps_longitude || '1.0261647022935751')
  const gpsZoom = parseInt(settings?.gps_zoom || '17')

  return (
    <div className="homepage-app">
      <main className="homepage-wrap">
        <article className="homepage-card">
          <div className="homepage-hero">
            {mediaType === 'image' ? (
              <img 
                className="homepage-media" 
                src={mediaUrl} 
                alt="Hero La Ferme du Petit Essart"
                onError={(e) => {
                  e.target.src = '/placeholder.svg'
                  e.target.alt = 'Image non disponible'
                }}
              />
            ) : (
              <video 
                className="homepage-media" 
                src={mediaUrl} 
                autoPlay 
                muted 
                loop 
                playsInline
              />
            )}
            <div 
              className="homepage-pill"
              onClick={handleTitleClick}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Triple-clic pour accès admin"
            >
              {headline}
            </div>
          </div>
          
          <div className="homepage-content">
            <div className="homepage-row info-row">
              <div className="grow">
                📍 <strong>{address}</strong>
              </div>
            </div>
            
            {openingHours && (
              <div className="homepage-row info-row">
                <div className="grow">
                  🕗 {openingHours}
                </div>
              </div>
            )}
            
            {/* 🆕 BOUTON RÉSERVATIONS EN PREMIER - avant les contacts */}
            <div className="homepage-row button-row">
              <a className="homepage-btn" href="/auth">
                🌾 Accéder aux Réservations
              </a>
            </div>
            
            {/* ══════════════════════════════════════════════════════════════
                🚫 MASQUÉ TEMPORAIREMENT - Email et Téléphone
                Décommenter ci-dessous pour réactiver l'affichage
            ══════════════════════════════════════════════════════════════ */}
            {/*
            <div className="homepage-row contact-row">
              {contactEmail && (
                <a href={`mailto:${contactEmail}`} className="contact-link">
                  ✉️ {contactEmail}
                </a>
              )}
              {contactPhone && (
                <a href={`tel:${contactPhone.replace(/\s/g, '')}`} className="contact-link">
                  📞 {contactPhone}
                </a>
              )}
            </div>
            */}
            
            {/* CARTE OSM INTÉGRÉE AVEC PNG DÉTOURÉ */}
            <div className="map-container">
              <h3 className="map-title">🗺️ Nous trouver</h3>
              <MapContainer 
                center={[gpsLat, gpsLon]} 
                zoom={gpsZoom} 
                scrollWheelZoom={false}
                style={{ height: '350px', width: '100%', borderRadius: '12px' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <Marker position={[gpsLat, gpsLon]} icon={fermeIcon}>
                  <Popup>
                    <div style={{ textAlign: 'center' }}>
                      <strong>🌿 La Ferme du Petit Essart</strong><br />
                      <small>{address}</small>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        </article>
        
        <footer className="homepage-footer">
          © 2025 La Ferme du Petit Essart – Grand-Couronne
        </footer>
      </main>
    </div>
  )
}

export default HomePage